import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../atoms';
import { MenuQuery, OperatorQuery } from '../../../services/query';
import { OperatorCommand } from '../../../services/command';
import {
    BulkAddRoleAuthorizationResponse,
    EndpointOption,
    RoleOption,
} from '../../../types/models/roleAuthorization.models';
import {
    formatSkippedRoleNamesFromResponse,
    normalizeBulkAddRoleAuthorizationResponse,
} from '../../../utils/roleAuthorization.utils';

interface IProps {
    open?: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
}

const getEndpointOptionKey = (option: EndpointOption) => `${option.httpmethod ?? ''}::${option.authorizedRoute ?? ''}`;

const mapEndpointItem = (item: Record<string, unknown>): EndpointOption | null => {
    const authorizedRoute = String(item.authorizedRoute ?? item.AuthorizedRoute ?? '').trim();
    if (!authorizedRoute) return null;

    return {
        authorizedRoute,
        httpmethod: String(item.httpmethod ?? item.HttpMethod ?? item.httpMethod ?? ''),
        privilege: String(item.privilege ?? item.Privilege ?? ''),
    };
};

const matchesEndpointSearch = (option: EndpointOption, search: string) => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return false;

    return option.authorizedRoute.toLowerCase().includes(normalizedSearch);
};

const normalizeEndpoints = (response: unknown): EndpointOption[] => {
    const rawList = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: unknown })?.data)
          ? (response as { data: Record<string, unknown>[] }).data
          : [];

    return rawList
        .map((item) => mapEndpointItem(item as Record<string, unknown>))
        .filter((item): item is EndpointOption => item !== null);
};

export const SCUIBulkRoleAuthorizationDialog: FC<IProps> = ({ open = false, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [endpoints, setEndpoints] = useState<EndpointOption[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointOption | null>(null);
    const [endpointSearch, setEndpointSearch] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetForm = useCallback(() => {
        setSelectedEndpoint(null);
        setEndpointSearch('');
        setSelectedRoles([]);
    }, []);

    const filteredEndpoints = useMemo(() => {
        const search = endpointSearch.trim();
        if (!search) return [];

        return endpoints.filter((option) => matchesEndpointSearch(option, search));
    }, [endpoints, endpointSearch]);

    const endpointAutocompleteValue = useMemo(() => {
        if (!selectedEndpoint) return null;

        return filteredEndpoints.some(
            (option) => getEndpointOptionKey(option) === getEndpointOptionKey(selectedEndpoint)
        )
            ? selectedEndpoint
            : null;
    }, [filteredEndpoints, selectedEndpoint]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [endpointsResponse, rolesResponse] = await Promise.all([
                MenuQuery.GetMenuAllEndPoints(),
                OperatorQuery.GetRolesList(),
            ]);

            if (endpointsResponse) {
                setEndpoints(normalizeEndpoints(endpointsResponse));
            }

            if (rolesResponse?.model?.data) {
                setRoles(rolesResponse.model.data);
            }
        } catch (error) {
            console.error('Bulk authorization data load error:', error);
            Swal.fire(t('authorizationManagement.bulkAuthorization.loadError'), '', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (open) {
            loadData();
        } else {
            resetForm();
        }
    }, [open, loadData, resetForm]);

    const handleClose = () => {
        if (!isSubmitting) {
            onClose?.();
        }
    };

    const showResultMessage = (response: BulkAddRoleAuthorizationResponse) => {
        const { addedCount, skippedCount } = response;
        const skippedRoleNames = formatSkippedRoleNamesFromResponse(response, roles);

        if (addedCount > 0 && skippedCount === 0) {
            Swal.fire(
                t('authorizationManagement.bulkAuthorization.successAdded', { count: addedCount }),
                '',
                'success'
            );
            return;
        }

        if (addedCount > 0 && skippedCount > 0) {
            Swal.fire(
                t('authorizationManagement.bulkAuthorization.successPartial', {
                    added: addedCount,
                    skipped: skippedCount,
                    skippedRoleNames,
                }),
                '',
                'success'
            );
            return;
        }

        if (addedCount === 0 && skippedCount > 0) {
            Swal.fire(t('authorizationManagement.bulkAuthorization.allSkipped', { skippedRoleNames }), '', 'info');
            return;
        }

        Swal.fire(t('general.couldntAdded'), '', 'error');
    };

    const tryGetBulkAddResultFromError = (error: any): BulkAddRoleAuthorizationResponse | null => {
        const raw = error?.data ?? error?.response?.data ?? error;
        const normalized = normalizeBulkAddRoleAuthorizationResponse(raw);
        if (normalized.addedCount > 0 || normalized.skippedCount > 0) {
            return normalized;
        }
        return null;
    };

    const handleSubmit = async () => {
        if (!selectedEndpoint?.authorizedRoute?.trim()) {
            Swal.fire(t('authorizationManagement.bulkAuthorization.endpointRequired'), '', 'warning');
            return;
        }

        if (selectedRoles.length === 0) {
            Swal.fire(t('authorizationManagement.bulkAuthorization.rolesRequired'), '', 'warning');
            return;
        }

        const privilegeBinary = selectedEndpoint.privilege?.trim();
        if (!privilegeBinary) {
            Swal.fire(t('authorizationManagement.bulkAuthorization.privilegeRequired'), '', 'warning');
            return;
        }

        const selectedRoleIds = selectedRoles.map((role) => role.roleId);

        setIsSubmitting(true);
        try {
            const response = normalizeBulkAddRoleAuthorizationResponse(
                await OperatorCommand.PostRoleAuthorizationBulkAdd({
                    authorizedRoute: selectedEndpoint.authorizedRoute,
                    privilegeBinary,
                    roleIds: selectedRoleIds,
                })
            );

            showResultMessage(response);
            onSuccess?.();
            onClose?.();
        } catch (error: any) {
            const bulkResult = tryGetBulkAddResultFromError(error);
            if (bulkResult) {
                showResultMessage(bulkResult);
                onSuccess?.();
                onClose?.();
                return;
            }

            const status = error?.status ?? error?.response?.status;
            const backendMessage = error?.data?.message || error?.data?.title || error?.response?.data?.message;

            if (status === 404) {
                Swal.fire(t('authorizationManagement.bulkAuthorization.endpointNotDeployed'), '', 'error');
            } else if (status === 400 && backendMessage) {
                Swal.fire(backendMessage, '', 'error');
            } else {
                Swal.fire(t('general.couldntAdded'), '', 'error');
            }
            console.error('Bulk authorization add error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon color="primary" />
                <Typography variant="h6">{t('authorizationManagement.bulkAuthorization.title')}</Typography>
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Autocomplete
                            options={filteredEndpoints}
                            value={endpointAutocompleteValue}
                            inputValue={endpointSearch}
                            openOnFocus={false}
                            onInputChange={(_, newInputValue, reason) => {
                                if (reason === 'input') {
                                    setEndpointSearch(newInputValue);
                                    setSelectedEndpoint(null);
                                    return;
                                }

                                if (reason === 'clear') {
                                    setEndpointSearch('');
                                    setSelectedEndpoint(null);
                                    return;
                                }

                                if (reason === 'reset' && selectedEndpoint) {
                                    setEndpointSearch(selectedEndpoint.authorizedRoute);
                                }
                            }}
                            onChange={(_, value) => {
                                setSelectedEndpoint(value);
                                setEndpointSearch(value?.authorizedRoute ?? '');
                            }}
                            getOptionLabel={(option) => option.authorizedRoute ?? ''}
                            isOptionEqualToValue={(option, value) =>
                                getEndpointOptionKey(option) === getEndpointOptionKey(value)
                            }
                            filterOptions={(options) => options}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;

                                return (
                                    <li key={key ?? getEndpointOptionKey(option)} {...optionProps}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography
                                                component="span"
                                                sx={{ fontWeight: 700, minWidth: 56, fontSize: 12 }}
                                            >
                                                {option.httpmethod}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                                {option.authorizedRoute}
                                            </Typography>
                                        </Box>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('authorizationManagement.bulkAuthorization.endpoint')}
                                    placeholder={t('authorizationManagement.bulkAuthorization.endpointPlaceholder')}
                                    autoComplete="off"
                                    slotProps={{
                                        htmlInput: {
                                            ...params.inputProps,
                                            autoComplete: 'new-password',
                                        },
                                    }}
                                />
                            )}
                        />

                        <TextField
                            label={t('authorizationManagement.apiMethod')}
                            value={selectedEndpoint?.httpmethod ?? ''}
                            disabled
                            fullWidth
                        />

                        <Autocomplete
                            multiple
                            options={roles}
                            value={selectedRoles}
                            getOptionLabel={(option) => option.roleNameKey}
                            isOptionEqualToValue={(option, value) => option.roleId === value.roleId}
                            onChange={(_, value) => setSelectedRoles(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('authorizationManagement.bulkAuthorization.roles')}
                                    placeholder={t('authorizationManagement.bulkAuthorization.rolesPlaceholder')}
                                />
                            )}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <SCUIButton
                    text={t('general.cancel')}
                    type="secondary"
                    sizeType="fixed"
                    size="m"
                    onClick={handleClose}
                    disabled={isSubmitting}
                />
                <SCUIButton
                    text={t('general.save')}
                    type="primary"
                    sizeType="fixed"
                    size="m"
                    onClick={handleSubmit}
                    disabled={isLoading || isSubmitting}
                />
            </DialogActions>
        </Dialog>
    );
};
